import {callSP} from 'setup/axios/AxiosCallSP'
import {Observable, from, of, forkJoin} from 'rxjs'
import {map, catchError, tap, retry, switchMap} from 'rxjs/operators'
import {getSStorage, setSStorage} from 'global.d'

export interface SessionMinute {
  id_session: number
  id_minuta: number
  usuario: string
  fecha_creacion: Date
}
export interface UpdateInsertMinute {
  id_minuta?: string
  usuario: string
  estado: string
  id_session?: string
  id_receta: string
  ponderacion: string
  comensales: string
  costo_plato: string
  costo_ponderado: string
  fija: string
  fecha?: string
  versionMinuta?: string
  costo?: string
}

interface ChangeItem {
  [key: number]: any
}

const rollbackMinute: ChangeItem[] = []
let countSessionMinute: number = 0
let countSaveChangeMinute: number = 0

export const getSessionMinute = (
  idMinute: string,
  idUser: string,
  newMinute: boolean = false
): Observable<boolean> => {
  const sessionData = getSStorage('dataMinutaSession')
  if (sessionData && !newMinute) {
    return of(true)
  } else if (newMinute) {
    const sessionParse: SessionMinute = JSON.parse(sessionData)
    idMinute = sessionParse.id_minuta.toString()
    idUser = sessionParse.usuario
  }
  return from(
    callSP('amd_setSesionMinuta', [
      {text: 'cmd', value: 'get_correlativo'},
      {text: 'id_minuta', value: idMinute},
      {text: 'usuario', value: idUser.toString()},
    ])
  ).pipe(
    retry(3),
    tap((result) => console.log('Antes del map:', result)),
    switchMap(({data}) => {
      if (data.result.length > 0) {
        const {id_session, id_minuta, usuario, fecha_creacion} = data.result[0]
        setSStorage(
          'dataMinutaSession',
          JSON.stringify({
            id_session: id_session,
            id_minuta: id_minuta,
            usuario: idUser.toString(),
            fecha_creacion: new Date(fecha_creacion),
          })
        )
        return of(true)
      }
      if (countSessionMinute < 3) {
        countSessionMinute++
        return getSessionMinute(idMinute, idUser, newMinute)
      }
      countSessionMinute = 0
      return of(false)
    }),
    catchError((error) => {
      console.error('Error fetching session minutes:', error)
      countSessionMinute = 0
      return of(false)
    })
  )
}

export const saveChangeMinute = (params: UpdateInsertMinute): Observable<boolean> => {
  const jsonData = getSStorage('dataMinutaSession')
  let minutaSession
  try {
    minutaSession = JSON.parse(jsonData)
  } catch (error) {
    console.error('Error parsing session data:', error)
    return of(false)
  }
  
  const newParams: UpdateInsertMinute = {
    ...params,
    id_session: minutaSession.id_session.toString(),
  }
  const parameters = [
    {text: 'cmd', value: 'update_insert_session'},
    {text: 'id_minuta', value: newParams.id_minuta},
    {text: 'usuario', value: newParams.usuario.toString()},
    {text: 'estado', value: newParams.estado},
    {text: 'id_session', value: newParams.id_session},
    {text: 'id_receta', value: newParams.id_receta},
    {text: 'ponderacion', value: newParams.ponderacion},
    {text: 'comensales', value: newParams.comensales},
    {text: 'costo_plato', value: newParams.costo_plato},
    {text: 'costo_ponderado', value: newParams.costo_ponderado},
    {text: 'fija', value: newParams.fija},
    {text: 'fecha', value: newParams.fecha || ''},
    {text: 'versionMinuta', value: newParams.versionMinuta?.toString() || ''},
    {text: 'costo', value: newParams.costo || ''},
  ]
  return from(callSP('amd_setSesionMinuta', parameters)).pipe(
    retry(3),
    switchMap(({data}) => {
      if (data.result.length > 0) {
        return of(true)
      }
      if (countSaveChangeMinute < 3) {
        countSaveChangeMinute++
        return saveChangeMinute(params)
      }
      countSaveChangeMinute = 0
      return of(false)
    }),
    catchError((error) => {
      countSaveChangeMinute = 0
      console.error('Error updating or inserting session minute:', error)
      return of(false)
    })
  )
}

export const sendMinute = (): Observable<boolean> => {
  const jsonData = getSStorage('dataMinutaSession')
  let minutaSession: SessionMinute
  try {
    minutaSession = JSON.parse(jsonData)
  } catch (error) {
    console.error('Error parsing session data:', error)
    return of(false)
  }
  return from(callSP('amd_setSesionMinuta', [
    { text: 'cmd', value: 'update_amdMinuta' },
    { text: 'id_session', value: minutaSession.id_session.toString() },
  ])).pipe(
    retry(3),
    map(({ data }) => (data?.status === '200' || (data?.result && data.result.length >= 0))),
    catchError(error => {
      console.error('Error updating or inserting session minute:', error)
      return of(false)
    })
  )
}

export const undoTypeSessionMinute = (undoType: string): Observable<boolean> => {
  const jsonData = getSStorage('dataMinutaSession')
  let minutaSession: SessionMinute
  try {
    minutaSession = JSON.parse(jsonData)
  } catch (error) {
    console.error('Error parsing session data:', error)
    return of(false)
  }

  return from(
    callSP('amd_setSesionMinuta', [
      {text: 'cmd', value: undoType},
      {text: 'id_session', value: minutaSession.id_session.toString()},
    ])
  ).pipe(
    retry(3),
    map(({data}) => data.result.length > 0),
    catchError((error) => {
      console.error('Error updating or inserting session minute:', error)
      return of(false)
    })
  )
}

export const cleanSessionMinute = () => {
  setSStorage('dataMinutaSession', '')
}

export const saveOldRowChangeMinute = (key: number, item: any) => {
  const copyItem = {...item}
  rollbackMinute.push({[key]: copyItem})
}

export const getLastLastMinute = () => {
  if (!rollbackMinute) return undefined
  return rollbackMinute.pop()
}

/**
 * Guarda un enroque (dos celdas) en paralelo.
 * Si cualquiera falla, devuelve false.
 */
export const saveSwapMinuteFood = (
  first: UpdateInsertMinute,
  second: UpdateInsertMinute
): Observable<boolean> => {
  return forkJoin([saveChangeMinute(first), saveChangeMinute(second)]).pipe(
    map(([a, b]) => !!a && !!b),
    catchError(() => of(false))
  )
}

// ====== ENROQUE: SP minimalista ======
export interface EnroqueParams {
  id_minuta: string
  id_receta: string
  usuario: string
  fija?: '0' | '1' // por defecto '0'
}

export const saveSwapMinuteFoodEnroque = (
  first: EnroqueParams,
  second: EnroqueParams
): Observable<boolean> => {
  const fija = '0'

  const doCalls = (sessionId: string) => {
    const build = (p: EnroqueParams) => ([
      { text: 'cmd', value: 'update_insert_enroque' },
      { text: 'id_minuta', value: p.id_minuta },
      { text: 'usuario', value: p.usuario },
      { text: 'id_session', value: sessionId },
      { text: 'id_receta', value: p.id_receta },
      { text: 'fija', value: fija }, // siempre 0
    ])

    const a$ = from(callSP('amd_setSesionMinuta', build(first)))
    const b$ = from(callSP('amd_setSesionMinuta', build(second)))

    return forkJoin([a$, b$]).pipe(
      map(([ra, rb]) => {
        const okA = String(ra?.data?.status ?? '') === '200'
        const okB = String(rb?.data?.status ?? '') === '200'
        return okA && okB
      }),
      catchError(() => of(false))
    )
  }

  // 1) Asegurar/crear una sesión (usa id_minuta/usuario del primer item)
  return getSessionMinute(first.id_minuta, first.usuario, false).pipe(
    switchMap((ok) => {
      if (!ok) return of(false)

      // 2) Leer UNA sola sesión y usarla para ambas llamadas (tal como tus logs)
      let idSession = ''
      try {
        const raw = getSStorage('dataMinutaSession')
        if (raw) {
          const parsed = JSON.parse(raw)
          idSession = String(parsed?.id_session ?? '')
        }
      } catch { /* ignore */ }

      if (!idSession) return of(false)

      // 3) Ejecutar A y B con la misma id_session
      return doCalls(idSession)
    }),
    // (Opcional) consolida al final para que se vea al refrescar:
    // switchMap(ok => ok ? sendMinute().pipe(map(() => true), catchError(() => of(true))) : of(false)),
    catchError(() => of(false))
  )
}